/**
 * The sentence to show for a Supabase auth failure (POS2-1176).
 *
 * Seven auth pages used to toast `error.message` straight from the SDK — *Invalid login
 * credentials*, *Email not confirmed*, *New password should be different from the old password*.
 * That text is GoTrue's, it is English, and it appears on the least forgiving screens in the
 * product: a user who cannot get in cannot read why they cannot get in.
 *
 * **Keyed off `code`, not off the prose.** `@supabase/auth-js` types `AuthError.code` as an
 * `ErrorCode` enum, so this is the same lookup `CODE_KEYS` performs on API errors in
 * `lib/api-error.ts` rather than a match on third-party wording that changes without notice. Its
 * own doc comment warns that `code` is `undefined` for failures that happen before a response
 * arrives — a dropped connection — and that case lands on the generic, which is the outcome a
 * string match would have got wrong.
 */
import type { AuthError } from "@supabase/supabase-js";

import type { TFunction } from "@/i18n/catalog";

/** Codes these screens actually produce. A code absent here is not a bug — it falls to the
 * generic, in the reader's language, which is the property that matters. */
const AUTH_CODE_KEYS: Record<string, string> = {
  invalid_credentials: "auth.errorInvalidCredentials",
  email_not_confirmed: "auth.errorEmailNotConfirmed",
  email_exists: "auth.errorEmailExists",
  user_already_exists: "auth.errorEmailExists",
  user_not_found: "auth.errorUserNotFound",
  user_banned: "auth.errorUserBanned",
  over_request_rate_limit: "auth.errorRateLimited",
  over_email_send_rate_limit: "auth.errorRateLimited",
  same_password: "auth.errorSamePassword",
  weak_password: "auth.errorWeakPassword",
  session_expired: "auth.errorSessionExpired",
  session_not_found: "auth.errorSessionExpired",
  refresh_token_not_found: "auth.errorSessionExpired",
  refresh_token_already_used: "auth.errorSessionExpired",
  otp_expired: "auth.errorLinkExpired",
  flow_state_not_found: "auth.errorLinkExpired",
  validation_failed: "auth.errorValidationFailed",
  mfa_verification_failed: "auth.errorMfaVerificationFailed",
  mfa_challenge_expired: "auth.errorMfaChallengeExpired",
};

/**
 * The headline for an auth failure — always from the catalogue.
 *
 * Takes the whole error rather than the code so a caller cannot forget the `undefined` case, and
 * returns a `string` rather than a key so the call site reads like the toast it becomes.
 */
export function authErrorMessage(t: TFunction, error: AuthError): string {
  const key = error.code ? AUTH_CODE_KEYS[error.code] : undefined;
  return key ? t(key) : t("auth.errorGeneric");
}
